/**
 * MissingConsentsView.jsx — COA-06
 * ---------------------------------------------------------------
 * Coordinator's cross-case operational queue of consent rows.
 * Dense data-grid (no cards, no KPI widgets, no charts) — mirrors
 * COA-05's Missing Documents shape, with one extra column
 * (Consent Version) and one extra filter (Consent Type) per the
 * brief.
 *
 * Owner brief columns (11):
 *   Patient · Case · Treatment · Consent · Consent Version
 *   · Assigned To · Status · Last Reminder · Due Date · Age · Actions
 *
 * Owner brief filters (6):
 *   Search · Treatment · Assigned To · Status · Age Bucket · Consent Type
 *
 * Allowed row actions:
 *   Open Case · View Consent Requirement · Send Reminder
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Bell, FileText, Search, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import DataGrid from '../../components/journey-ux/DataGrid';
import Pill from '../../components/journey-ux/Pill';
import FilterChips from '../../components/journey-ux/FilterChips';

/* ─── tone tables ─────────────────────────────────────────────── */
// "Signed" replaces "Approved" for consent semantics; "Expired" tone
// stays in the table for forward-compatibility (the backend has no
// expiry concept today — see 04_STATUS_MAPPING.md).
const STATUS_TONE = {
  missing:   { tone: 'rose',    label: 'Missing' },
  requested: { tone: 'amber',   label: 'Requested' },
  signed:    { tone: 'emerald', label: 'Signed' },
  expired:   { tone: 'slate',   label: 'Expired' },
};
const AGE_TONE = {
  fresh: { tone: 'slate', label: 'Fresh (<3d)' },
  aging: { tone: 'amber', label: 'Aging (3-7d)' },
  stale: { tone: 'rose',  label: 'Stale (>7d)' },
};
const SOURCE_TONE = {
  template: 'sky', offer: 'amber', campaign: 'violet', override: 'rose',
};

/* ─── helpers ─────────────────────────────────────────────────── */
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '—';
  }
};
const _short = (id) => (id ? `#${String(id).slice(-6)}` : '—');
const dueLabel = (offsetDays) => {
  if (offsetDays === null || offsetDays === undefined) return '—';
  const n = Number(offsetDays);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '+0d';
  return n > 0 ? `+${n}d` : `${n}d`;
};
const versionLabel = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return s.startsWith('v') ? s : `v${s}`;
};

/* ─── component ───────────────────────────────────────────────── */
export default function MissingConsentsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState({ all: 0, missing: 0, requested: 0, signed: 0 });
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [sending, setSending] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  // Filters — exactly 6 per brief
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [treatment, setTreatment] = useState('__all__');
  const [assignedTo, setAssignedTo] = useState('__all__');
  const [ageBucket, setAgeBucket] = useState('__all__');
  const [consentType, setConsentType] = useState('__all__');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clinic/coordinator/missing-consents');
      if (data?.success) {
        setRows(Array.isArray(data.items) ? data.items : []);
        setBuckets(data.buckets || { all: 0, missing: 0, requested: 0, signed: 0 });
      } else {
        setRows([]);
      }
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, reloadTick]);

  const treatmentOptions = useMemo(() => {
    const set = new Map();
    rows.forEach((r) => {
      if (r.treatment_slug) set.set(r.treatment_slug, r.treatment_name || r.treatment_slug);
    });
    return Array.from(set.entries()).map(([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const assigneeOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.default_assignee) set.add(r.default_assignee); });
    return Array.from(set).sort();
  }, [rows]);

  const consentTypeOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.consent_name) set.add(r.consent_name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== 'all' && r.status !== status) return false;
      if (treatment !== '__all__' && r.treatment_slug !== treatment) return false;
      if (assignedTo !== '__all__') {
        if (assignedTo === '__unassigned__') {
          if (r.default_assignee) return false;
        } else if (r.default_assignee !== assignedTo) {
          return false;
        }
      }
      if (consentType !== '__all__' && r.consent_name !== consentType) return false;
      if (ageBucket !== '__all__' && r.age_bucket !== ageBucket) return false;
      if (q) {
        const hay = `${r.patient_name || ''} ${r.consent_name || ''} ${r.treatment_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, status, treatment, assignedTo, consentType, ageBucket, search]);

  const statusChips = useMemo(() => [
    { id: 'all',       label: 'All',       count: buckets.all },
    { id: 'missing',   label: 'Missing',   count: buckets.missing },
    { id: 'requested', label: 'Requested', count: buckets.requested },
    { id: 'signed',    label: 'Signed',    count: buckets.signed },
  ], [buckets]);

  const sendReminder = useCallback(async (row) => {
    if (!row) return;
    setSending(row.row_id);
    try {
      const { data } = await api.post('/clinic/coordinator/missing-consents/reminder', {
        case_id: row.case_id, row_id: row.row_id,
      });
      if (data?.success) {
        toast.success(`Reminder sent · "${row.consent_name}" · count = ${data.reminder?.count ?? '—'}`);
        setReloadTick((t) => t + 1);
      } else {
        toast.error(data?.message || 'Reminder failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Reminder failed');
    } finally {
      setSending(null);
    }
  }, []);

  /* ─── 11 grid columns (Patient · Case · Treatment · Consent ·
       Consent Version · Assigned To · Status · Last Reminder ·
       Due Date · Age · Actions) ───────────────────────────────── */
  const columns = useMemo(() => [
    {
      id: 'patient',
      header: 'Patient',
      width: 'w-36',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[12px] font-medium text-slate-900 truncate" data-testid="mc-cell-patient">{r.patient_name || '—'}</div>
          <div className="text-[10.5px] text-slate-500 font-mono">{r.country || ''}</div>
        </div>
      ),
    },
    {
      id: 'case',
      header: 'Case',
      width: 'w-20',
      render: (r) => (
        <span className="font-mono text-[11px] text-slate-700" data-testid="mc-cell-case">{_short(r.case_id)}</span>
      ),
    },
    {
      id: 'treatment',
      header: 'Treatment',
      width: 'w-32',
      render: (r) => (
        <span className="text-[11px] text-slate-700 truncate" data-testid="mc-cell-treatment">{r.treatment_name || r.treatment_slug || '—'}</span>
      ),
    },
    {
      id: 'consent',
      header: 'Consent',
      width: 'w-40',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[12px] text-slate-900 truncate" data-testid="mc-cell-consent">{r.consent_name}</div>
          {r.source && (
            <div className="mt-0.5">
              <Pill tone={SOURCE_TONE[r.source] || 'slate'} className="text-[9.5px]">{r.source}</Pill>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'consent_version',
      header: 'Version',
      width: 'w-16',
      render: (r) => (
        <span className="font-mono text-[11px] text-slate-700" data-testid="mc-cell-version">{versionLabel(r.consent_version)}</span>
      ),
    },
    {
      id: 'assigned',
      header: 'Assigned To',
      width: 'w-32',
      render: (r) => {
        if (!r.default_assignee && !r.default_role) {
          return <span className="text-rose-500 text-[10.5px] uppercase tracking-wider" data-testid="mc-cell-assigned">Unassigned</span>;
        }
        return (
          <div className="leading-tight">
            <div className="text-[11px] text-slate-800" data-testid="mc-cell-assigned">{r.default_role || '—'}</div>
            {r.default_assignee && (
              <div className="text-[10.5px] text-slate-500 font-mono">{_short(r.default_assignee)}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      width: 'w-24',
      render: (r) => {
        const t = STATUS_TONE[r.status] || { tone: 'slate', label: r.status || '—' };
        return <Pill tone={t.tone} data-testid={`mc-cell-status-${r.status}`}>{t.label}</Pill>;
      },
    },
    {
      id: 'reminder',
      header: 'Last Reminder',
      width: 'w-24',
      render: (r) => (
        <div className="leading-tight">
          <div className="text-[11px] text-slate-700" data-testid="mc-cell-reminder">{fmtRel(r.last_reminder_at)}</div>
          {r.reminder_count > 0 && (
            <div className="text-[10px] text-slate-400">×{r.reminder_count}</div>
          )}
        </div>
      ),
    },
    {
      id: 'due',
      header: 'Due Date',
      width: 'w-16',
      align: 'right',
      render: (r) => <span className="font-mono text-[11px] text-slate-700">{dueLabel(r.due_offset_days)}</span>,
    },
    {
      id: 'age',
      header: 'Age',
      width: 'w-24',
      render: (r) => {
        const t = AGE_TONE[r.age_bucket] || { tone: 'slate', label: '—' };
        return (
          <div className="leading-tight">
            <Pill tone={t.tone}>{r.age_bucket}</Pill>
            <div className="text-[10px] text-slate-500 mt-0.5">{r.age_days}d</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 'w-44',
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/clinic/cases/${r.case_id}/workflow`); }}
            data-testid="mc-action-open-case"
            className="text-[10.5px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-300 text-slate-700 inline-flex items-center gap-1"
            title={t('provider.missingConsents.aOpenWorkflow')}
          >
            <ExternalLink className="h-3 w-3" /> Open
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setActiveRow(r); }}
            data-testid="mc-action-view-req"
            className="text-[10.5px] px-2 py-0.5 rounded border border-slate-200 hover:border-slate-300 text-slate-700 inline-flex items-center gap-1"
            title={t('provider.missingConsents.aViewRequirement')}
          >
            <FileText className="h-3 w-3" /> View
          </button>
          {r.status !== 'signed' && (
            <button
              type="button"
              disabled={sending === r.row_id}
              onClick={(e) => { e.stopPropagation(); sendReminder(r); }}
              data-testid="mc-action-send-reminder"
              className="text-[10.5px] px-2 py-0.5 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white inline-flex items-center gap-1"
              title={t('provider.missingConsents.aSendReminder')}
            >
              <Bell className="h-3 w-3" />
              {sending === r.row_id ? 'Sending…' : (r.reminder_count > 0 ? 'Remind again' : 'Remind')}
            </button>
          )}
        </div>
      ),
    },
  ], [navigate, sending, sendReminder, t]);

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="missing-consents-workspace">
      {/* FILTER STRIP */}
      <div className="bg-white border-b border-slate-100 sticky top-[88px] z-10" data-testid="missing-consents-filters">
        <div className="px-5 pt-2 flex flex-wrap items-center gap-2">
          <div className="relative">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('provider.missingConsents.searchAttr')}
              data-testid="mc-filter-search"
              className="h-7 w-64 pl-7 pr-2 text-[12px] border border-slate-200 rounded bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400"
            />
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          </div>

          <select
            value={treatment}
            onChange={(e) => setTreatment(e.target.value)}
            data-testid="mc-filter-treatment"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All treatments</option>
            {treatmentOptions.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>

          <select
            value={consentType}
            onChange={(e) => setConsentType(e.target.value)}
            data-testid="mc-filter-consent-type"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">{t('provider.missingConsents.allConsentTypes')}</option>
            {consentTypeOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            data-testid="mc-filter-assigned"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All assignees</option>
            <option value="__unassigned__">Unassigned only</option>
            {assigneeOptions.map((uid) => (
              <option key={uid} value={uid}>{uid.slice(0, 16)}…</option>
            ))}
          </select>

          <select
            value={ageBucket}
            onChange={(e) => setAgeBucket(e.target.value)}
            data-testid="mc-filter-age"
            className="h-7 text-[11.5px] px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            <option value="__all__">All ages</option>
            <option value="fresh">Fresh (&lt;3d)</option>
            <option value="aging">Aging (3-7d)</option>
            <option value="stale">Stale (&gt;7d)</option>
          </select>

          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            data-testid="mc-refresh"
            className="ml-auto h-7 inline-flex items-center gap-1 px-2 text-[11px] border border-slate-200 rounded text-slate-700 hover:border-slate-300"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="px-5 py-2">
          <FilterChips
            chips={statusChips}
            activeId={status}
            onChange={setStatus}
            testIdPrefix="mc-status-chip"
          />
        </div>
      </div>

      {/* Result strip */}
      <div className="px-5 py-1.5 bg-white border-b border-slate-100 text-[11px] text-slate-500 flex items-center gap-2" data-testid="mc-result-strip">
        <span>{filtered.length}</span>
        <span>{t('common.of')}</span>
        <span>{t('provider.missingConsents.consentRowCount', { count: rows.length })}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.missing}</strong> {t('provider.missingConsents.missingBadge')}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.requested}</strong> {t('provider.missingConsents.requested')}</span>
        <span className="text-slate-300">·</span>
        <span><strong>{buckets.signed}</strong> {t('provider.missingConsents.signed')}</span>
      </div>

      {/* GRID + DRAWER */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="mc"
            columns={columns}
            rows={filtered}
            rowKey={(r) => `${r.case_id}::${r.row_id}`}
            activeRowId={activeRow ? `${activeRow.case_id}::${activeRow.row_id}` : null}
            onRowClick={setActiveRow}
            loading={loading}
            loadingMessage="Loading missing consents…"
            emptyMessage={
              <div className="text-center py-8 max-w-md mx-auto">
                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-1.5" />
                <div className="text-[13px] font-semibold text-slate-700">{t('provider.missingConsents.emptyTitle')}</div>
                <div className="text-[11.5px] text-slate-500">{t('provider.missingConsents.emptyHelp')}</div>
              </div>
            }
            maxHeightClass="h-[calc(100vh-240px)]"
          />
        </div>

        {activeRow && (
          <aside
            className="w-[360px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto"
            data-testid="mc-drawer"
          >
            <header className="px-4 py-2 border-b border-slate-200 flex items-start justify-between gap-2 sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.missingConsents.consentRequirement')}</div>
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">{activeRow.consent_name}</div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">
                  {activeRow.patient_name} · {activeRow.treatment_name || activeRow.treatment_slug} · {_short(activeRow.case_id)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveRow(null)}
                data-testid="mc-drawer-close"
                className="text-[11px] text-slate-500 hover:text-slate-800"
              >
                ✕
              </button>
            </header>
            <div className="p-4 space-y-2 text-[12px]">
              <Field label="Status">
                <Pill tone={(STATUS_TONE[activeRow.status] || { tone: 'slate' }).tone}>
                  {(STATUS_TONE[activeRow.status] || { label: activeRow.status }).label}
                </Pill>
              </Field>
              <Field label="Consent Version">{versionLabel(activeRow.consent_version)}</Field>
              <Field label="Source">
                <Pill tone={SOURCE_TONE[activeRow.source] || 'slate'}>{activeRow.source || '—'}</Pill>
              </Field>
              <Field label="Assigned Role">{activeRow.default_role || '—'}</Field>
              <Field label="Assigned User">{activeRow.default_assignee ? _short(activeRow.default_assignee) : <span className="text-rose-500 uppercase text-[10px] tracking-wider">Unassigned</span>}</Field>
              <Field label="Due offset">{dueLabel(activeRow.due_offset_days)}</Field>
              <Field label="Age">{activeRow.age_days}d · <span className="text-slate-500">{activeRow.age_bucket}</span></Field>
              <Field label="Last reminder">{fmtRel(activeRow.last_reminder_at)} {activeRow.reminder_count > 0 ? `(×${activeRow.reminder_count})` : ''}</Field>
              {activeRow.satisfied_at && (
                <>
                  <Field label="Signed at">{formatDateTimeLocale(activeRow.satisfied_at)}</Field>
                  <Field label="Signed by">{_short(activeRow.satisfied_by)}</Field>
                </>
              )}
              <div className="pt-2 border-t border-slate-100 flex gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(`/clinic/cases/${activeRow.case_id}/workflow`)}
                  data-testid="mc-drawer-open-case"
                  className="text-[11px] px-2 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded inline-flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" /> Open case
                </button>
                {activeRow.status !== 'signed' && (
                  <button
                    type="button"
                    disabled={sending === activeRow.row_id}
                    onClick={() => sendReminder(activeRow)}
                    data-testid="mc-drawer-send-reminder"
                    className="text-[11px] px-2 py-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white rounded inline-flex items-center gap-1"
                  >
                    <Bell className="h-3 w-3" /> {activeRow.reminder_count > 0 ? 'Remind again' : 'Send reminder'}
                  </button>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">{label}</span>
      <span className="text-right text-[12px] text-slate-900 min-w-0">{children}</span>
    </div>
  );
}
